import * as React from "react";
import { Link } from "react-router";
import Input from "../Input-new/Input";
import Dropdown from "../Dropdown/Dropdown";
import { fetchListedFirm, saveFirmRow, CRM_CREATE_DUPLICATE } from "../../requests/firms";

const EDITABLE_COLUMNS = ['NAME', 'ADDRESS', 'CITY', 'STATE', 'ZIP', 'PHONE', 'WEBSITE'];

/**
 * The Firm tab's data on a record page whose firm id sits in `data[firmKey]`:
 * the firm as /firms lists it, kept in the page's `firm` / `firmOriginal`
 * state so edits survive switching tabs, and saved before the record itself.
 */
export class LinkedFirmEdits {
    constructor(page, firmKey) {
        this.page = page;
        this.firmKey = firmKey;
        this.loadedId = undefined;
        this.request = 0;

        this.change = this.change.bind(this);
        this.discard = this.discard.bind(this);
    }

    /** Called from the page's componentDidUpdate; some pages change `data` in place, so the id is compared, not the object. */
    follow() {
        const data = this.page.state.data;
        if (!data) return;

        const firmId = data[this.firmKey] ? String(data[this.firmKey]) : '';
        if (firmId !== this.loadedId) this.load(firmId);
    }

    load(firmId) {
        this.loadedId = firmId;
        const request = ++this.request;

        if (!firmId) {
            this.page.setState({ firm: null, firmOriginal: null });
            return;
        }

        fetchListedFirm(firmId)
            .then((firm) => {
                if (request !== this.request) return;
                this.page.setState({ firm, firmOriginal: firm ? { ...firm } : null });
            })
            .catch(() => {
                if (request !== this.request) return;
                this.page.setState({ error: "Could not load the firm." });
            });
    }

    change(key, value) {
        this.page.setState((prev) => ({ firm: { ...prev.firm, [key]: value } }));
    }

    discard() {
        this.page.setState((prev) => ({ firm: prev.firmOriginal ? { ...prev.firmOriginal } : null }));
    }

    edited() {
        const { firm, firmOriginal } = this.page.state;
        if (!firm || !firmOriginal) return false;

        return EDITABLE_COLUMNS.some((column) => (firm[column] || '') !== (firmOriginal[column] || ''));
    }

    /** The firm goes first, so a firm edit Kore refuses stops the save with every edit still on screen. */
    saveThen(saveRecord) {
        if (!this.edited()) return saveRecord();

        const firm = this.page.state.firm;
        this.page.setState({ isBusy: true, error: null, success: null });

        return saveFirmRow(firm)
            .then((res) => (
                res && !res.success && res.confirm === CRM_CREATE_DUPLICATE
                    ? (window.confirm(res.message) ? saveFirmRow(firm, { confirmCrmCreate: true }) : null)
                    : res
            ))
            .then((res) => {
                if (!res || !res.success) {
                    this.page.setState({
                        isBusy: false,
                        viewMode: false,
                        error: res ? (res.message || "The firm was not saved.") : null,
                    });
                    return;
                }

                const saved = res.data[0];
                this.page.setState({ firm: saved, firmOriginal: { ...saved }, isBusy: false }, saveRecord);
            });
    }
}

/**
 * The Firm tab on the vendor and agent pages: the linked firm itself, so a
 * change made on the firm's page shows here, and an edit made here changes
 * the firm for everyone attached to it.
 */
const LinkedFirm = ({ firm, viewMode, deleted, onChange }) => {
    const data = firm || {};
    const editing = Boolean(firm) && !viewMode;

    const field = (name, label, valueType = 'text') => (
        <Input
            className="LinkedFirm_item"
            valueType={valueType}
            name={name}
            label={label}
            value={data[name]}
            handleParentChange={onChange}
            viewMode={!editing}
            deleted={deleted}
        />
    );

    return (
        <div className="LinkedFirm">
            {editing ? field('NAME', 'Firm') : (
                <div className="input-container default LinkedFirm_item">
                    <label className="input-label default">Firm</label>
                    <div className="view default">
                        {firm
                            ? <Link to={{ pathname: `${arc.path.base}firm/${firm.ARCIMEDES_ROW_ID}` }}>{firm.NAME}</Link>
                            : '-'}
                    </div>
                </div>
            )}
            {field('ADDRESS', 'Address')}
            {field('CITY', 'City')}
            <Dropdown
                className="LinkedFirm_item"
                data={data}
                optionsType="states"
                optionKey="abbr"
                optionLabelKey="name"
                value={data.STATE}
                valueKey="STATE"
                label="State"
                handleParentChange={(query, type, x, key) => onChange(key, query[key])}
                viewMode={!editing}
                deleted={deleted}
            />
            {field('ZIP', 'Zip')}
            {field('PHONE', 'Phone', 'phone')}
            {field('WEBSITE', 'Website')}
        </div>
    );
};

export default LinkedFirm;
